import React, { useState, useEffect } from "react";
import Sidebar from "../Dashboard/Sidebar";
import Header from "../Dashboard/Header";
import Footer from "../Dashboard/Footer";
import CustomAlert from "./CustomAlert";
import ConfirmDeleteAlert from "./ConfirmDeleteAlert";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { Plus, FolderOpen, Settings, Calendar, Link as LinkIcon } from "lucide-react";

function Projects() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingProject, setOpeningProject] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [alert, setAlert] = useState({ message: "", type: "success" });
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, projectId: null, projectName: "" });
  const [showNoReportsError, setShowNoReportsError] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const isLargeScreen = windowWidth >= 992;

  const getSidebarWidth = () => {
    if (!sidebarOpen) return "0px";
    return sidebarCollapsed ? "80px" : "280px";
  };

  const handleSidebarItemClick = (index) => {
    setActiveSidebarItem(index);
  };

      useEffect(() => {
            document.title = "DCode | Projects";
          }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/projects/view/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-User-Email": localStorage.getItem("Email"),
            // "Authorization": `Bearer ${localStorage.getItem("access_token")}`,
          },
        });



        if (!response.ok) {
          throw new Error("Failed to fetch projects");
        }

        const data = await response.json();
        const mappedProjects = data.data.map((project) => ({
          id: project.id,
          name: project.project_name,
          analyzedAt: new Date(project.created_at).toLocaleDateString(),
          analyzedTime: new Date(project.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: project.status || "Active",
          link: project.github_url || "",
          api_key: project.api_key,
        }));
        setProjects(mappedProjects);
        setLoading(false);
        setShowNoReportsError(false);
      } catch (err) {
        setAlert({ message: err.message, type: "error" });
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleDeleteProject = async (projectId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/projects/${projectId}/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete project");
      }

      setProjects(projects.filter((project) => project.id !== projectId));
      setAlert({ message: "Project deleted successfully", type: "success" });
    } catch (err) {
      setAlert({ message: err.message, type: "error" });
    }
  };

  const closeConfirmDelete = () => {
    setConfirmDelete({ isOpen: false, projectId: null, projectName: "" });
  };

  const confirmDeleteProject = () => {
    handleDeleteProject(confirmDelete.projectId);
    closeConfirmDelete();
  };

  const closeAlert = () => {
    setAlert({ message: "", type: "success" });
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);

  const handleSidebarCollapseChange = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  const handleCreateProject = async () => {
    try {
      navigate("/project/create");
    } catch (err) {
      setAlert({ message: "Failed to create project", type: "error" });
    }
  };

  const handleOpenProject = async (project) => {
    try {
      // console.log("Opening project:", project);
      setOpeningProject(project.id);
      setShowNoReportsError(false); // Reset the no reports error state
      const response = await fetch(`${BACKEND_URL}/projects/${project.id}/analysis-summaries/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": project.api_key,
        },
      });
      const latestCommit = await fetch(`${BACKEND_URL}/projects/${project.id}/commits/recent/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": project.api_key,
        },
      });
      const latestCommitRes = await latestCommit.json();
      // console.log("Latest Commit Response:", latestCommitRes);
      if (
        latestCommitRes.recent_commits &&
        latestCommitRes.recent_commits.length > 0
      ) {
        const latest_commit_id = latestCommitRes.recent_commits[0].commit_id;


        localStorage.setItem("latest_commit_id", latest_commit_id)
        // Continue with visualization fetch...
        const latestcommitviz = await axios.get(
          `${BACKEND_URL}/projects/${project.id}/${latest_commit_id}/visualize/`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": project.api_key,
            },
          }
        );
        // console.log("Latest Commit Visualization Data:", latestcommitviz.data);
        if (latestcommitviz.data) {
          // Store data in IndexedDB and navigate
          const { openDB } = await import('idb');
          // console.log("Storing latest commit visualization data in IndexedDB");
          const dbPromise = openDB('AnalysisDB', 1, {
            upgrade(db) {
              // console.log("Upgrading/Creating IndexedDB store");
              if (!db.objectStoreNames.contains('analysis')) {
                db.createObjectStore('analysis', { keyPath: 'id' });
              }
            },
          });

          const db = await dbPromise;
          // console.log("Storing latest commit visualization data in IndexedDB");
          await db.put('analysis', {
            id: 'analyzedData',
            data: latestcommitviz.data.data
          });
        }
      }
      // console.log("Fetching analysis summaries for project ID:", project.id);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load project visualization");
      }
      // console.log("Fetched analysis summaries successfully");
      const visualizationResponse = await response.json();
      const visualizationData = visualizationResponse.summaries;
      // Check if there are no analysis summaries/reports
      if (!visualizationData || visualizationData.length === 0) {
        setShowNoReportsError(true);
        setCountdown(15); // Reset countdown when showing error
        setOpeningProject(null);
        return;
      }
      const { openDB } = await import('idb');
      const dbPromise = openDB('AnalysisDB', 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('analysis')) {
            db.createObjectStore('analysis', { keyPath: 'id' });
          }
        },
      });
      // console.log("Storing visualization data in IndexedDB");
      const db = await dbPromise;
      await db.put('analysis', {
        id: 'trendSummaries',
        data: visualizationData
      });
      await db.put('analysis', {
        id: 'projectInfo',
        data: project
      });
      // Make selected project instantly available to the Sidebar/pages
      localStorage.setItem('selectedProjectId', String(project.id));
      localStorage.setItem('selectedProjectName', project.name || '');
      navigate(`/quality-trends`);
    } catch (err) {
      setAlert({ message: "Could not fetch project data!", type: "error" });
      console.error("Error fetching project visualization:", err);
    } finally {
      setOpeningProject(null);
    }
  };

  const handleSettingsClick = (projectId) => {
    const p = projects.find(p => p.id === projectId);
    if (p) {
      localStorage.setItem('selectedProjectId', String(p.id));
      localStorage.setItem('selectedProjectName', p.name || '');
    }
    navigate(`/project/${projectId}/settings`);
  };

  const getFirstLetter = (name) => {
    return name ? name.charAt(0).toUpperCase() : "";
  };

  // Auto redirect after 10 seconds when showing no reports error
  useEffect(() => {
    let timer;
    if (showNoReportsError && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (showNoReportsError && countdown === 0) {
      navigate("/project/view");
      setShowNoReportsError(false);
      setCountdown(10);
    }
    return () => clearTimeout(timer);
  }, [showNoReportsError, countdown, navigate]);

  return (
    <div className="d-flex flex-column min-vh-100">
      <CustomAlert message={alert.message} type={alert.type} onClose={closeAlert} />
      <ConfirmDeleteAlert
        isOpen={confirmDelete.isOpen}
        projectName={confirmDelete.projectName}
        onConfirm={confirmDeleteProject}
        onCancel={closeConfirmDelete}
      />

      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Projects"
        />
      </div>

      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={handleSidebarItemClick}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
          onClick={toggleSidebar}
        ></div>
      )}

      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          paddingTop: "70px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        <div className="container-fluid p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0 text-dark fw-bold">Projects</h2>
            {projects.length > 0 && !showNoReportsError && (
              <button className="upload-button" onClick={handleCreateProject}>
                <Plus size={18} className="me-2" />
                Create project
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center" style={{ minHeight: "60vh" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : showNoReportsError ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
              {/* Error Icon */}
              <div className="mb-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    background: "linear-gradient(135deg, #75c4c0 0%, #75c4c0 100%)",
                    boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
                  }}
                >
                  <FolderOpen color="white" size={40} />
                </div>
              </div>

              {/* Error Content */}
              <div className="text-center max-w-md">
                <h3 className="fw-bold mb-3" style={{ color: "#2c3e50" }}>
                  No code quality reports found
                </h3>

                <div
                  className="alert alert-warning border-0 mb-4"
                  style={{
                    backgroundColor: "#fff3cd",
                    borderLeft: "4px solid #ffc107",
                    borderRadius: "8px"
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div>
                      <p className="mb-0" style={{ color: "#856404", textAlign: "center" }}>
                        There are no code quality reports for this project yet. <br />
                        Refer to our documentation to learn how to integrate this project's code quality analysis into your CI pipeline. Once configured, DCode will begin receiving code quality reports, allowing you to visualize and analyze your project's quality trends.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Countdown Timer */}
                <div className="mt-3">
                  <p className="text-muted small mb-0">
                    Redirecting to projects in {countdown} seconds...
                  </p>
                </div>

                {/* Help Text */}
                {/* <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
                  <p
                    className="text-muted small mb-0"
                    style={{ textAlign: "center" }}
                  >
                    <i className="fas fa-lightbulb me-2"></i>
                    Need help with integration? Check our documentation or contact support
                  </p>
                </div> */}
              </div>
            </div>
          ) : alert.message && alert.type === "error" ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
              {/* Error Icon */}
              <div className="mb-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    background: "linear-gradient(135deg, #75c4c0 0%, #75c4c0 100%)",
                    boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
                  }}
                >
                  <FolderOpen color="white" size={40} />
                </div>
              </div>

              {/* Error Content */}
              <div className="text-center max-w-md">
                <h3 className="fw-bold mb-3" style={{ color: "#2c3e50" }}>
                  You didn't upload any reports yet!
                </h3>

                {/* <div
                  className="alert alert-danger border-0 mb-4"
                  style={{
                    backgroundColor: "#fef2f2",
                    borderLeft: "4px solid #ef4444",
                    borderRadius: "8px"
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div>
                      <p className="mb-0" style={{ color: "#7f1d1d", textAlign: "center" }}>
                        {alert.message}
                      </p>
                    </div>
                  </div>
                </div> */}

                {/* Help Text */}
                <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
                  <p
                    className="text-muted small mb-0"
                    style={{ textAlign: "center" }}
                  >
                    <i className="fas fa-lightbulb me-2"></i>
                    Please read our documentation to learn about how you can configure DCode with your CI Pipeline.
                  </p>
                </div>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
              <div className="text-center">
                <div className="mb-4">
                  <FolderOpen size={64} color="#6c757d" />
                </div>
                <h4 className="text-muted mb-3">No projects created so far</h4>
                <p className="text-muted mb-4 fs-6">
                  Get started with DCode by creating your first project.
                </p>
                <button className="upload-button" onClick={handleCreateProject}>
                  <Plus size={18} className="me-2" />
                  Create your first project
                </button>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {projects.map((project) => (
                <div key={project.id} className="col-lg-4 col-md-6 col-sm-12">
                  <div className="card project-card h-100 shadow-lg">
                    <div className="card-body project-body p-4">
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                          <div className="project-icon me-2">
                            {getFirstLetter(project.name)}
                          </div>
                          <h5 className="card-title project-title mb-0 text-dark fw-semibold">{project.name}</h5>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-secondary border-0"
                          onClick={() => handleSettingsClick(project.id)}
                          title="Project Settings"
                        >
                          <Settings size={18} />
                        </button>

                      </div>
                      {project.link && (
                        <div className="mb-3">
                          <small className="text-muted d-flex align-items-center">
                            <LinkIcon size={14} className="me-1" />
                            <a
                              href={project.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-truncate project-link"
                              style={{ maxWidth: "250px" }}
                            >
                              {project.link}
                            </a>
                          </small>
                        </div>
                      )}
                      <div className="mb-0">
                        <small className="text-muted d-flex align-items-center">
                          <Calendar size={14} className="me-1" /> Last Analyzed on <span style={{ display: "none" }}>: </span>
                          {project.analyzedAt} at {project.analyzedTime}
                        </small>
                      </div>
                    </div>
                    <div className="card-footer bg-transparent border-0 p-4 pt-0">
                      <div className="row g-2">
                        <div className="col-12">
                          <button
                            className="btn project-btn w-100"
                            onClick={() => handleOpenProject(project)}
                            disabled={openingProject === project.id}
                          >
                            {openingProject === project.id ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Loading...
                              </>
                            ) : (
                              <>
                                <FolderOpen size={16} className="me-2" />
                                Open project
                              </>
                            )}
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default Projects;
